export type ProvisionStageState = 'pending' | 'running' | 'succeeded' | 'failed';

export type ProvisionStage = { name: string; state: ProvisionStageState };

export type ProvisionStatus = {
  done: boolean;
  stages: ProvisionStage[];
};

// The single seam between the hub and GET /provisioning-status. The endpoint answers
// { phase, steps: [{ program, state, ... }], ... }: `phase` is the caller-facing word
// to switch on, and only `ready` means the tenant actually serves — `awaiting_convergence`
// means the build finished but the site is not up yet, so the poll keeps waiting.
// Anything unrecognised parses as "not ready, no stages" for the same reason.

// The writer's step vocabulary, mapped to what the stage list renders. `step_skipped`
// is deliberately absent: a skipped step will never run for this tenant, so it is
// dropped rather than shown as forever-pending.
const STEP_STATES: Readonly<Record<string, ProvisionStageState>> = {
  pending: 'pending',
  step_started: 'running',
  step_succeeded: 'succeeded',
  step_failed: 'failed',
};

function parseStages(value: unknown): ProvisionStage[] {
  if (!Array.isArray(value)) return [];
  const stages: ProvisionStage[] = [];
  for (const entry of value) {
    const name = (entry as { program?: unknown })?.program;
    const state = (entry as { state?: unknown })?.state;
    const mapped = typeof state === 'string' ? STEP_STATES[state] : undefined;
    if (typeof name === 'string' && mapped) {
      stages.push({ name, state: mapped });
    }
  }
  return stages;
}

export function parseProvisionStatus(body: unknown): ProvisionStatus {
  const record = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>;
  return { done: record.phase === 'ready', stages: parseStages(record.steps) };
}
